import React, { useCallback, useEffect, useState } from 'react';
import Link from '../detail-pages/Link';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@/ui/components/dialog';
import { v2Session } from '../../components/lists/lists';
import {
    FOLLOWED_STORIES,
    sized,
    timeAgo,
    type RestaurantStory,
    type RestaurantStoryGroup,
} from '../../components/feed/feed';
import { RestaurantAvatar } from './PostCard';

/**
 * Restaurant stories (prototype StoriesBar / StoryViewer, mock "Stories"): 24-hour photos
 * from restaurants. A strip of tiles (one per restaurant in the feed, one per story on a
 * restaurant page) opens a full-screen viewer that steps through them. Tiles a viewer has
 * opened lose their coral ring (remembered in this browser only).
 */

const SEEN_KEY = 'nearbites:seen-stories';
const STORY_SECONDS = 6;

function readSeen(): Set<string> {
    try {
        return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]'));
    } catch {
        return new Set();
    }
}

function useSeen(): [Set<string>, (ids: string[]) => void] {
    const [seen, setSeen] = useState<Set<string>>(new Set());
    useEffect(() => setSeen(readSeen()), []);
    const mark = useCallback((ids: string[]) => {
        setSeen(prev => {
            const next = new Set([...prev, ...ids]);
            try {
                // Stories last a day, so a few hundred ids is plenty.
                localStorage.setItem(
                    SEEN_KEY,
                    JSON.stringify([...next].slice(-300)),
                );
            } catch {
                // Private mode: rings just come back next time.
            }
            return next;
        });
    }, []);
    return [seen, mark];
}

const Tile = ({
    story,
    label,
    unseen,
    showRestaurant,
    onOpen,
}: {
    story: RestaurantStory;
    label: string;
    unseen: boolean;
    showRestaurant: boolean;
    onOpen: () => void;
}) => (
    <li className="shrink-0">
        <button
            type="button"
            onClick={onOpen}
            className={`relative block h-48 w-32 overflow-hidden rounded-lg border-2 p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-ring md:h-52 md:w-36 ${unseen ? 'border-primary' : 'border-border-subtle'}`}
        >
            <img
                src={sized(story.image, '288x416-fit=cover')}
                alt=""
                className="size-full rounded-md object-cover"
            />
            <span className="absolute inset-0.5 rounded-md bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <span className="absolute right-2.5 bottom-2.5 left-2.5 flex flex-col items-start gap-1 text-left">
                {showRestaurant && (
                    <RestaurantAvatar restaurant={story.restaurant} size="sm" />
                )}
                <span className="line-clamp-2 text-body-2 font-semibold text-white">
                    {label}
                </span>
            </span>
        </button>
    </li>
);

/** The full-screen viewer: one group's stories in order, then the next group's. */
export const StoryViewer = ({
    groups,
    start,
    onClose,
    onSeen,
}: {
    groups: RestaurantStoryGroup[];
    /** [group, story] to open at; null = closed. */
    start: [number, number] | null;
    onClose: () => void;
    onSeen: (id: string) => void;
}) => {
    const [at, setAt] = useState<[number, number] | null>(start);
    useEffect(() => setAt(start), [start]);
    const group = at ? groups[at[0]] : null;
    const story = group && at ? group.stories[at[1]] : null;

    const step = useCallback(
        (by: 1 | -1) => {
            setAt(prev => {
                if (!prev) return prev;
                const [g, i] = prev;
                const next = i + by;
                if (next >= 0 && next < groups[g].stories.length)
                    return [g, next];
                if (by === 1 && g + 1 < groups.length) return [g + 1, 0];
                if (by === -1 && g > 0)
                    return [g - 1, groups[g - 1].stories.length - 1];
                if (by === 1) onClose();
                return prev;
            });
        },
        [groups, onClose],
    );

    useEffect(() => {
        if (!story) return;
        onSeen(story.id);
        const timer = setTimeout(() => step(1), STORY_SECONDS * 1000);
        return () => clearTimeout(timer);
    }, [story, step, onSeen]);

    useEffect(() => {
        if (!story) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') step(1);
            if (e.key === 'ArrowLeft') step(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [story, step]);

    const name = group?.restaurant.name ?? 'A restaurant';
    return (
        <Dialog open={story !== null} onOpenChange={open => !open && onClose()}>
            <DialogContent className="bg-black text-white md:top-1/2 md:h-[90vh] md:max-h-[90vh] md:max-w-[min(90vw,480px)] md:overflow-hidden md:rounded-lg">
                {story && group && at && (
                    <div className="relative flex h-full flex-col">
                        <div className="absolute inset-x-0 top-0 z-10 flex flex-col gap-3 bg-gradient-to-b from-black/70 to-transparent p-3 pr-12">
                            <div className="flex gap-1">
                                {group.stories.map((s, i) => (
                                    <span
                                        key={s.id}
                                        className={`h-0.5 flex-1 rounded-full ${i <= at[1] ? 'bg-white' : 'bg-white/40'}`}
                                    />
                                ))}
                            </div>
                            <div className="flex items-center gap-3">
                                <RestaurantAvatar
                                    restaurant={group.restaurant}
                                    size="sm"
                                />
                                <DialogTitle className="text-body-1 font-semibold text-white">
                                    {name}
                                </DialogTitle>
                                <span className="text-body-2 text-white/70">
                                    {timeAgo(story.createdAt)}
                                </span>
                            </div>
                        </div>
                        <img
                            src={sized(story.image, '720x1280-fit=cover')}
                            alt={story.caption ?? `A story from ${name}`}
                            className="min-h-0 w-full flex-1 object-cover"
                        />
                        <button
                            type="button"
                            onClick={() => step(-1)}
                            className="absolute top-0 bottom-24 left-0 w-1/3 outline-none focus-visible:ring-2 focus-visible:ring-white"
                        >
                            <span className="sr-only">Previous story</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => step(1)}
                            className="absolute top-0 right-0 bottom-24 w-2/3 outline-none focus-visible:ring-2 focus-visible:ring-white"
                        >
                            <span className="sr-only">Next story</span>
                        </button>
                        <div className="relative flex flex-col gap-3 bg-gradient-to-t from-black/80 to-transparent p-4">
                            <DialogDescription
                                className={
                                    story.caption
                                        ? 'text-body-1 text-white'
                                        : 'sr-only'
                                }
                            >
                                {story.caption ?? `A story from ${name}`}
                            </DialogDescription>
                            {story.item?.name && story.item.path && (
                                <Link
                                    to={story.item.path}
                                    className="flex items-center justify-between gap-3 rounded-lg bg-white/15 px-4 py-3 text-body-2 font-semibold text-white no-underline hover:bg-white/25"
                                >
                                    <span className="truncate">
                                        {story.item.name}
                                    </span>
                                    {story.item.price && (
                                        <span className="shrink-0 font-normal">
                                            {story.item.price.display}
                                        </span>
                                    )}
                                </Link>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

/**
 * A row of story tiles. With several restaurants (the feed) each tile is a restaurant;
 * with one (its page) each tile is a story, labelled with its age.
 */
export const StoryTiles = ({
    groups,
    title,
}: {
    groups: RestaurantStoryGroup[];
    title?: string;
}) => {
    const [seen, markSeen] = useSeen();
    const [start, setStart] = useState<[number, number] | null>(null);
    const onSeen = useCallback((id: string) => markSeen([id]), [markSeen]);
    const single = groups.length === 1;
    if (!groups.length) return null;
    return (
        <section aria-label={title ?? 'Stories'}>
            {title && (
                <h2 className="mb-3 text-subtitle-1 font-semibold text-ink-strong">
                    {title}
                </h2>
            )}
            <ul className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2">
                {single
                    ? groups[0].stories.map((story, i) => (
                          <Tile
                              key={story.id}
                              story={story}
                              label={timeAgo(story.createdAt)}
                              unseen={!seen.has(story.id)}
                              showRestaurant={false}
                              onOpen={() => setStart([0, i])}
                          />
                      ))
                    : groups.map((group, g) => {
                          const latest =
                              group.stories[group.stories.length - 1];
                          const firstUnseen = group.stories.findIndex(
                              s => !seen.has(s.id),
                          );
                          return (
                              <Tile
                                  key={group.restaurant.legacyRestaurantId}
                                  story={latest}
                                  label={
                                      group.restaurant.name ?? 'A restaurant'
                                  }
                                  unseen={firstUnseen >= 0}
                                  showRestaurant
                                  onOpen={() =>
                                      setStart([g, Math.max(firstUnseen, 0)])
                                  }
                              />
                          );
                      })}
            </ul>
            <StoryViewer
                groups={groups}
                start={start}
                onClose={() => setStart(null)}
                onSeen={onSeen}
            />
        </section>
    );
};

/** The feed's strip: live stories from the restaurants the viewer follows. */
export const StoriesStrip = () => {
    const [groups, setGroups] = useState<RestaurantStoryGroup[]>([]);
    useEffect(() => {
        let cancelled = false;
        v2Session<{ followedRestaurantStories: RestaurantStoryGroup[] }>(
            FOLLOWED_STORIES,
        )
            .then(d => !cancelled && setGroups(d.followedRestaurantStories))
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, []);
    if (!groups.length) return null;
    return (
        <div className="border-b border-border-subtle pb-6">
            <StoryTiles groups={groups} title="Stories" />
        </div>
    );
};
